import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, SkipForward } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const criteriaSummary = (c) => {
  if (!c) return null;
  const parts = [];
  if (c.category) parts.push(c.category);
  if (c.diameter_min != null || c.diameter_max != null) parts.push(`Ø ${c.diameter_min ?? '…'}–${c.diameter_max ?? '…'} mm`);
  if (c.length_min != null || c.length_max != null) parts.push(`L ${c.length_min ?? '…'}–${c.length_max ?? '…'} mm`);
  if (c.brand) parts.push(`brand=${c.brand}`);
  if (c.name_contains) parts.push(`name~"${c.name_contains}"`);
  return parts.length ? parts.join(' · ') : null;
};

const BuyerItemBulkImport = ({ open, onOpenChange, onImported }) => {
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setPreview(null); setResult(null); };

  const handleFileChange = (e) => {
    reset();
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API_URL}/buyer-items/template`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'buyer_items_template.csv';
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
      toast.success('Template downloaded');
    } catch (_e) { toast.error('Failed to download template'); }
  };

  const runPreview = async () => {
    if (!file) { toast.error('Choose a file first'); return; }
    setPreviewing(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/buyer-items/bulk-preview`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      if (res.data.summary.valid === 0) toast.error('No valid rows to import');
      else toast.success(`${res.data.summary.valid} of ${res.data.summary.total} row(s) ready`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally { setPreviewing(false); }
  };

  const runCommit = async () => {
    if (!preview) return;
    const validRows = preview.rows.filter(r => r.status === 'ok').map(r => ({
      code: r.code,
      description: r.description,
      active: !!r.active,
      match_criteria: r.match_criteria || null,
    }));
    if (validRows.length === 0) { toast.error('No valid rows to import'); return; }
    setCommitting(true);
    try {
      const res = await axios.post(`${API_URL}/buyer-items/bulk-commit`, { rows: validRows });
      setResult(res.data);
      if (res.data.summary.created > 0) {
        toast.success(`${res.data.summary.created} Buyer Item(s) created`);
        if (onImported) onImported();
      }
      if (res.data.summary.failed > 0) toast.error(`${res.data.summary.failed} row(s) failed`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setCommitting(false); }
  };

  const close = () => { reset(); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="bi-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-teal-600" /> Bulk Import Buyer Items
          </DialogTitle>
          <DialogDescription>
            Upload a CSV or Excel file. Required columns: <span className="font-medium">Buyer Item Code, Buyer Item Description</span>.
            Optional columns: <span className="font-medium">Active, Category, Diameter Min, Diameter Max, Length Min, Length Max, Brand, Name Contains</span>.
            Existing codes are skipped, never overwritten.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center justify-between gap-3 rounded-md border bg-slate-50 p-3">
            <div className="text-sm text-slate-700">
              <p className="font-medium">Template</p>
              <p className="text-xs text-slate-500">CSV with all 10 columns and 2 sample rows (one with match rules, one manual-only).</p>
            </div>
            <Button type="button" variant="outline" onClick={downloadTemplate} data-testid="bi-bulk-template-btn">
              <Download className="w-4 h-4 mr-2" /> Download Template
            </Button>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-md border p-3">
            <div className="flex items-center gap-3">
              <label className="cursor-pointer">
                <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFileChange} className="hidden" data-testid="bi-bulk-file-input" />
                <span className="inline-flex items-center px-3 py-2 rounded-md border bg-white text-sm hover:bg-slate-50">
                  <Upload className="w-4 h-4 mr-2" /> Choose file
                </span>
              </label>
              <span className="text-xs text-slate-600" data-testid="bi-bulk-file-name">{file ? file.name : 'No file selected'}</span>
            </div>
            <Button type="button" onClick={runPreview} disabled={!file || previewing} className="bg-teal-600 hover:bg-teal-700" data-testid="bi-bulk-preview-btn">
              {previewing ? 'Previewing…' : 'Preview'}
            </Button>
          </div>

          {preview && (
            <div className="space-y-2" data-testid="bi-bulk-preview">
              <div className="flex items-center gap-2 text-sm">
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="bi-bulk-count-valid">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {preview.summary.valid} to create
                </Badge>
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100" data-testid="bi-bulk-count-skipped">
                  <SkipForward className="w-3.5 h-3.5 mr-1" /> {preview.summary.skipped ?? 0} skipped
                </Badge>
                <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100" data-testid="bi-bulk-count-invalid">
                  <AlertTriangle className="w-3.5 h-3.5 mr-1" /> {preview.summary.invalid} errors
                </Badge>
                <span className="text-xs text-slate-500">of {preview.summary.total} row(s)</span>
              </div>
              <ScrollArea className="h-[340px] rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[52px]">Row</TableHead>
                      <TableHead className="w-[16%]">Code</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-[24%]">Match Rules</TableHead>
                      <TableHead className="w-[64px]">Active</TableHead>
                      <TableHead className="w-[96px]">Status</TableHead>
                      <TableHead>Errors</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.rows.map(r => (
                      <TableRow
                        key={r.row_number}
                        className={cn(
                          r.status === 'error' && 'bg-rose-50/60',
                          r.status === 'skip' && 'bg-amber-50/50',
                        )}
                      >
                        <TableCell className="tabular-nums">{r.row_number}</TableCell>
                        <TableCell className="tabular-nums font-medium">{r.code || '—'}</TableCell>
                        <TableCell className="text-slate-600">{r.description || '—'}</TableCell>
                        <TableCell>
                          {criteriaSummary(r.match_criteria) ? (
                            <span className="text-[11px] text-slate-600">{criteriaSummary(r.match_criteria)}</span>
                          ) : <span className="text-[11px] text-slate-400">Manual only</span>}
                        </TableCell>
                        <TableCell>{r.active ? 'Yes' : 'No'}</TableCell>
                        <TableCell>
                          {r.status === 'ok' && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">OK</Badge>}
                          {r.status === 'skip' && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Skip</Badge>}
                          {r.status === 'error' && <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Error</Badge>}
                        </TableCell>
                        <TableCell>
                          {r.errors && r.errors.length > 0 ? (
                            <ul className="text-xs text-rose-700 list-disc pl-4">
                              {r.errors.map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                          ) : r.status === 'skip' ? (
                            <span className="text-xs text-amber-700">Code already exists — will be left untouched</span>
                          ) : <span className="text-slate-400 text-xs">—</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          )}

          {result && (
            <div className="rounded-md border p-3 bg-slate-50 space-y-1" data-testid="bi-bulk-result">
              <div className="text-sm">
                Attempted: <span className="font-medium">{result.summary.attempted}</span> ·
                Created: <span className="font-medium text-emerald-700">{result.summary.created}</span> ·
                Skipped: <span className="font-medium text-amber-700">{result.summary.skipped ?? 0}</span> ·
                Failed: <span className="font-medium text-rose-700">{result.summary.failed}</span>
              </div>
              {result.errors && result.errors.length > 0 && (
                <ul className="text-xs text-rose-700 list-disc pl-4">
                  {result.errors.slice(0, 20).map((e, i) => (
                    <li key={i}><span className="font-medium">{e.code || `Row ${e.index + 1}`}</span>: {e.errors.join(', ')}</li>
                  ))}
                  {result.errors.length > 20 && <li>… and {result.errors.length - 20} more</li>}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={close}>Close</Button>
          <Button type="button" onClick={runCommit} disabled={!preview || preview.summary.valid === 0 || committing || !!result} className="bg-teal-600 hover:bg-teal-700" data-testid="bi-bulk-commit-btn">
            {committing ? 'Importing…' : preview ? `Import ${preview.summary.valid} valid row(s)` : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BuyerItemBulkImport;
